import { FormEvent, KeyboardEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { NotMedicalBanner } from '../../components/dtp/PatientSupport';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowUp, Loader2, MessageCircle, Pill, RotateCcw } from 'lucide-react';
import { dtpApi } from '../../lib/dtpApi';
import { dtpPath, useDtpParams } from '../../lib/clinicSite';
import { useDtpAuthStore } from '../../store/useDtpAuthStore';
import { patientUnreadMessagesKey } from '../../hooks/usePatientUnreadMessages';
import type { CareMessage, CareThreadPage } from '../../types/careMessaging';

/** How often the open thread checks for replies. Polling, not a socket: replies take minutes, not seconds. */
export const MESSAGES_POLL_MS = 15_000;
const MAX_LENGTH = 2000;

interface PendingMessage { tempId: string; body: string; status: 'sending' | 'failed' }

export const careThreadKey = (physicianId?: string, patientId?: string) => ['patient-care-thread', physicianId, patientId] as const;

export function DtpMessages() {
  const { physicianId } = useDtpParams();
  const patientId = useDtpAuthStore(s => s.patient?._id);
  const impersonated = useDtpAuthStore(s => s.impersonated);
  const queryClient = useQueryClient();
  const threadKey = careThreadKey(physicianId, patientId);

  const thread = useQuery<CareThreadPage>({
    queryKey: threadKey,
    retry: false,
    refetchInterval: MESSAGES_POLL_MS,
    queryFn: async () => {
      const res = await dtpApi.getCareMessages();
      if (!res.success || !res.data) throw new Error('Messages unavailable');
      return res.data;
    },
  });

  // Earlier pages the patient asked for; the polled query only ever holds the newest page.
  const [older, setOlder] = useState<CareMessage[]>([]);
  const [olderHasMore, setOlderHasMore] = useState<boolean | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [pending, setPending] = useState<PendingMessage[]>([]);

  const messages = useMemo(() => {
    const byId = new Map<string, CareMessage>();
    for (const m of [...older, ...(thread.data?.messages ?? [])]) byId.set(m._id, m);
    return [...byId.values()].sort((a, b) => a.seq - b.seq);
  }, [older, thread.data]);
  const hasMore = olderHasMore ?? thread.data?.hasMore ?? false;
  const clinicName = thread.data?.clinicName || 'your clinic';
  const careTeam = thread.data?.careTeam;
  // A telemed prescriber answers instead of the clinic: say who, by name when we know it.
  const telemedName = careTeam?.destination === 'telemed'
    ? [careTeam.providerName, careTeam.networkName && `${careTeam.providerName ? 'at ' : ''}${careTeam.networkName}`].filter(Boolean).join(' ')
    : '';
  const recipient = careTeam?.destination === 'telemed'
    ? (telemedName ? `${telemedName}, your prescribing provider` : 'your prescribing provider')
    : `the clinicians at ${clinicName}`;

  async function loadOlder() {
    if (!messages.length) return;
    setLoadingOlder(true);
    try {
      const res = await dtpApi.getCareMessages(messages[0].seq);
      if (res.success && res.data) {
        setOlder(prev => [...res.data!.messages, ...prev]);
        setOlderHasMore(res.data.hasMore);
      }
    } catch {
      // The button stays; the patient can try again.
    } finally {
      setLoadingOlder(false);
    }
  }

  // Mark what the patient has been shown as read. Never while the clinic is
  // viewing on their behalf: that would hide the clinic's reply from them.
  const lastMarked = useRef(0);
  const newestSeq = messages.length ? messages[messages.length - 1].seq : 0;
  const readSeq = thread.data?.conversation?.patientReadSeq ?? 0;
  useEffect(() => {
    if (impersonated || !newestSeq || newestSeq <= readSeq || newestSeq <= lastMarked.current) return;
    lastMarked.current = newestSeq;
    dtpApi.markCareMessagesRead(newestSeq)
      .then(() => queryClient.invalidateQueries({ queryKey: patientUnreadMessagesKey(physicianId, patientId) }))
      .catch(() => { lastMarked.current = 0; });
  }, [impersonated, newestSeq, readSeq, physicianId, patientId, queryClient]);

  // Keep the newest message in view as replies arrive.
  const endRef = useRef<HTMLDivElement>(null);
  const shownCount = messages.length + pending.length;
  useEffect(() => { endRef.current?.scrollIntoView?.({ block: 'end' }); }, [shownCount]);

  async function send(body: string, tempId = `tmp-${Date.now()}`) {
    setPending(prev => [...prev.filter(p => p.tempId !== tempId), { tempId, body, status: 'sending' }]);
    try {
      const res = await dtpApi.sendCareMessage(body);
      if (!res.success || !res.data) throw new Error(res.error || 'Not sent');
      const sent = res.data;
      queryClient.setQueryData<CareThreadPage>(threadKey, (prev: CareThreadPage | undefined) => prev
        ? { ...prev, messages: [...prev.messages, sent] }
        : { conversation: null, messages: [sent], hasMore: false, clinicName });
      setPending(prev => prev.filter(p => p.tempId !== tempId));
      queryClient.invalidateQueries({ queryKey: threadKey });
    } catch {
      setPending(prev => prev.map(p => (p.tempId === tempId ? { ...p, status: 'failed' } : p)));
    }
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col">
      <h1 className="portal-display text-[36px] font-bold leading-none sm:text-[44px]">Messages</h1>
      <p className="mt-3 text-[17px] text-[var(--portal-muted)]">Questions about your treatment, side effects or refills go to {recipient}.</p>
      <NotMedicalBanner />
      <EmergencyNote />

      {thread.isPending ? (
        <div role="status" aria-label="Loading messages" className="mt-6 space-y-3">
          {[0, 1, 2].map(i => <div key={i} className={`h-14 w-2/3 rounded-3xl bg-[rgba(36,26,21,0.06)] motion-safe:animate-pulse ${i === 1 ? 'ml-auto' : ''}`} />)}
        </div>
      ) : thread.isError ? (
        <div role="alert" className="mt-6 rounded-2xl bg-[#fde4dc] p-4 text-sm text-[#8c2a14]">
          We couldn’t load your messages. <button className="min-h-[44px] font-semibold underline" onClick={() => thread.refetch()}>Try again</button>
        </div>
      ) : (
        <section aria-label={`Conversation with ${clinicName}`} className="mt-6">
          {messages.length === 0 && pending.length === 0 ? (
            <EmptyThread clinicName={clinicName} />
          ) : (
            <>
              {hasMore && (
                <div className="mb-4 text-center">
                  <button type="button" onClick={loadOlder} disabled={loadingOlder}
                    className="inline-flex h-10 items-center gap-2 rounded-full bg-white/80 px-4 text-sm font-semibold ring-1 ring-[var(--portal-line)] hover:bg-white disabled:opacity-60">
                    {loadingOlder && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}Load earlier messages
                  </button>
                </div>
              )}
              <ol className="space-y-3" aria-live="polite">
                {messages.map(m => <li key={m._id}><MessageBubble message={m} /></li>)}
                {pending.map(p => (
                  <li key={p.tempId}>
                    <PendingBubble pending={p} onRetry={() => send(p.body, p.tempId)} onDiscard={() => setPending(prev => prev.filter(x => x.tempId !== p.tempId))} />
                  </li>
                ))}
              </ol>
            </>
          )}
          <div ref={endRef} />
        </section>
      )}

      {!thread.isPending && !thread.isError && (
        impersonated ? (
          <p className="mt-6 rounded-2xl bg-[var(--portal-tint-3)] px-4 py-3 text-sm">
            You’re viewing this portal on the patient’s behalf. Only the patient can send messages here; reply from the care inbox instead.
          </p>
        ) : (
          <Composer onSend={body => send(body)} sending={pending.some(p => p.status === 'sending')} />
        )
      )}
    </div>
  );
}

function EmergencyNote() {
  return (
    <p className="mt-4 flex items-start gap-2 rounded-2xl bg-[#fff4e5] px-4 py-3 text-sm text-[#7a4100]">
      <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
      <span>Not for emergencies. If you need urgent help, call 911 or go to the nearest emergency room.</span>
    </p>
  );
}

function EmptyThread({ clinicName }: { clinicName: string }) {
  return (
    <div className="portal-card px-6 py-12 text-center">
      <span className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-[18px] bg-[var(--portal-tint-1)]"><MessageCircle aria-hidden="true" className="h-6 w-6" /></span>
      <p className="portal-display text-xl font-semibold">Message your care team</p>
      <p className="mx-auto mt-2 max-w-md text-sm text-[var(--portal-muted)]">
        Your messages are read by the care team at {clinicName} during clinic hours, and their replies appear here. We’ll text or email you when they reply.
      </p>
    </div>
  );
}

function timeLabel(iso: string) {
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const REFILL_STATUS: Record<string, { label: string; className: string }> = {
  open: { label: 'Waiting for your clinic', className: 'bg-[var(--portal-tint-1)] text-[var(--portal-ink)]' },
  approved: { label: 'Approved', className: 'bg-[#dcf2e3] text-[#14532d]' },
  declined: { label: 'Not approved', className: 'bg-[#fde4dc] text-[#8c2a14]' },
};

function MessageBubble({ message }: { message: CareMessage }) {
  const mine = message.author === 'patient';
  const refill = message.kind === 'refill_request' ? message.refill : undefined;
  return (
    <article className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
      <div className={`max-w-[85%] whitespace-pre-wrap break-words rounded-3xl px-4 py-3 text-[15px] leading-relaxed sm:max-w-[75%] ${mine
        ? 'rounded-br-lg bg-[var(--portal-primary)] text-[var(--portal-primary-fg)]'
        : 'rounded-bl-lg bg-[var(--portal-card-solid)] text-[var(--portal-ink)] ring-1 ring-[var(--portal-line)]'}`}>
        {refill && (
          <p className="mb-2 flex flex-wrap items-center gap-2 text-xs font-semibold">
            <Pill aria-hidden="true" className="h-3.5 w-3.5" />{refill.requestType === 'dose_change' ? 'Dose change request' : 'Refill request'}
            <span className={`rounded-full px-2 py-0.5 ${REFILL_STATUS[refill.status]?.className}`}>{REFILL_STATUS[refill.status]?.label}</span>
          </p>
        )}
        {message.body}
      </div>
      <p className="mt-1 px-2 text-xs text-[var(--portal-muted)]">
        {mine ? 'You' : message.authorName} · {timeLabel(message.createdAt)}
        {mine && message.delivery === 'sending' && ' · Sending…'}
        {mine && message.delivery === 'failed' && <span className="font-medium text-[#8c2a14]"> · Not delivered. Please send it again, or chat with support.</span>}
      </p>
    </article>
  );
}

function PendingBubble({ pending, onRetry, onDiscard }: { pending: PendingMessage; onRetry: () => void; onDiscard: () => void }) {
  const failed = pending.status === 'failed';
  return (
    <article className="flex flex-col items-end">
      <div className={`max-w-[85%] whitespace-pre-wrap break-words rounded-3xl rounded-br-lg px-4 py-3 text-[15px] leading-relaxed sm:max-w-[75%] ${failed ? 'bg-[#fde4dc] text-[#8c2a14]' : 'bg-[var(--portal-primary)] text-[var(--portal-primary-fg)] opacity-70'}`}>
        {pending.body}
      </div>
      {failed ? (
        <p role="alert" className="mt-1 flex items-center gap-3 px-2 text-xs text-[#8c2a14]">
          Not sent.
          <button type="button" onClick={onRetry} className="inline-flex min-h-[32px] items-center gap-1 font-semibold underline"><RotateCcw aria-hidden="true" className="h-3 w-3" />Retry</button>
          <button type="button" onClick={onDiscard} className="min-h-[32px] underline">Discard</button>
        </p>
      ) : (
        <p className="mt-1 px-2 text-xs text-[var(--portal-muted)]">Sending…</p>
      )}
    </article>
  );
}

/**
 * Pinned to the bottom of the screen while the thread scrolls. On phones it
 * sits just above the tab bar (and the home indicator), not under it.
 */
function Composer({ onSend, sending }: { onSend: (body: string) => void; sending: boolean }) {
  const [draft, setDraft] = useState('');
  const body = draft.trim();
  const tooLong = draft.length > MAX_LENGTH;

  function submit(event?: FormEvent) {
    event?.preventDefault();
    if (!body || tooLong) return;
    onSend(body);
    setDraft('');
  }
  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) submit();
  }

  return (
    <form onSubmit={submit}
      className="sticky bottom-[calc(env(safe-area-inset-bottom)_+_5rem)] z-10 mt-6 rounded-[28px] border border-[var(--portal-line)] bg-[var(--portal-card-solid)] p-2 shadow-[0_18px_40px_-28px_rgba(36,26,21,0.45)] lg:bottom-4">
      <div className="flex items-end gap-2">
        <label htmlFor="care-message" className="sr-only">Message to your care team</label>
        <textarea id="care-message" value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={onKeyDown}
          rows={draft.includes('\n') || draft.length > 60 ? 4 : 1} placeholder="Write a message…"
          className="max-h-40 min-h-[44px] flex-1 resize-none rounded-2xl border-0 bg-transparent px-3 py-2.5 text-[15px] text-[var(--portal-ink)] placeholder:text-[var(--portal-muted)] focus:outline-none focus:ring-0" />
        <button type="submit" disabled={!body || tooLong} aria-label="Send message"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[var(--portal-primary)] text-[var(--portal-primary-fg)] transition hover:opacity-90 focus:outline-none focus-visible:ring-4 focus-visible:ring-[rgba(224,86,58,0.25)] disabled:opacity-40">
          {sending ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> : <ArrowUp aria-hidden="true" className="h-5 w-5" />}
        </button>
      </div>
      {draft.length > MAX_LENGTH - 200 && (
        <p className={`px-3 pb-1 text-xs ${tooLong ? 'text-[#8c2a14]' : 'text-[var(--portal-muted)]'}`}>{draft.length}/{MAX_LENGTH}</p>
      )}
    </form>
  );
}

/** "Go to Messages" for other pages (treatments, home cards). */
export function MessagesLink({ children = 'Go to Messages', className }: { children?: ReactNode; className?: string }) {
  const { physicianId } = useDtpParams();
  return <Link to={dtpPath(physicianId, '/messages')} className={className ?? 'font-semibold underline'}>{children}</Link>;
}
